import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

const PricingComparison = () => {

  const navigate = useNavigate();
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleButtonGetStarted = () => {
    navigate('/get-started');
  };

  const treatments = [
    {
      name: t('pricingComparison.treatment1'),
      dentfinder: { min: 150, max: 150 },
      otherPlatform: { min: 650, max: 650 },
    },
    {
      name: t('pricingComparison.treatment2'),
      dentfinder: { min: 650, max: 650 },
      otherPlatform: { min: 2200, max: 2200 },
    },
    {
      name: t('pricingComparison.treatment3'),
      dentfinder: { min: 250, max: 250 },
      otherPlatform: { min: 800, max: 800 },
    },
    {
      name: t('pricingComparison.treatment4'),
      dentfinder: { min: 3500, max: 3500 },
      otherPlatform: { min: 14000, max: 14000 },
    },
  ];

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 30 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut", delay: 0.4 },
    },
  };

  return (
    <section ref={ref} className="bg-[#EAF8F9] py-16 md:py-24">
      <div className="container-custom">
        {/* Top Section */}
        <motion.div
          className="flex flex-col items-center text-center mb-12 md:mb-16 max-w-[666px] mx-auto"
          variants={headerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="bg-white px-4 py-2 rounded-full w-fit mb-6">
            <span className="text-sm text-[#5A5A59]">{t('pricingComparison.badge')}</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-[56px] font-semibold bg-gradient-to-r from-[#000087] to-[#000000] bg-clip-text text-transparent leading-tight max-w-4xl mb-6">
            {t('pricingComparison.heading')}
          </h2>
          
          <p className="text-[16px] text-[#000929] max-w-3xl leading-relaxed">
            {t('pricingComparison.description')}
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 mb-8"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Card 1 */}
          <motion.div
            className="bg-white border-2 border-[#14C5D1] rounded-xl py-9 md:bg-transparent md:border-0 md:rounded-2xl"
            variants={cardVariants}
          >
            <div className="flex flex-col items-center mb-8">
              <div className="hidden md:block h-12 mb-4"></div>
              <div className="items-center justify-center mb-4 md:hidden">
               <img src="/assets/search.svg" alt="DentFinder" />
              </div>
              <div className="hidden md:block h-7 mb-3"></div>
              <h3 className="text-xl font-semibold text-[#14C5D1] mb-3 md:hidden">{t('pricingComparison.dentfinder')}</h3>
              <div className="hidden md:block h-10"></div>
              <p className="text-3xl md:text-4xl font-bold text-[#0C1B1F] md:hidden">{t('pricingComparison.ourPrice')}</p>
            </div>

            <div className="space-y-0">
              {treatments.map((treatment, index) => (
                <div
                  key={index}
                  className={`py-4 px-6 ${index % 2 === 0 ? 'bg-[#FAFAFA]' : 'bg-white md:bg-transparent'} ${index !== treatments.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div className="flex flex-row justify-between items-center md:flex-col md:justify-start">
                    <span className="text-sm font-medium text-[#181D27] md:mb-1">
                      {treatment.name}
                    </span>
                    <span className="text-sm font-semibold text-[#535862] md:hidden">
                      {treatment.dentfinder.min === treatment.dentfinder.max
                        ? `${t('pricingComparison.startsFrom')} €${treatment.dentfinder.min.toLocaleString()}`
                        : `${t('pricingComparison.startsFrom')} €${treatment.dentfinder.min} - €${treatment.dentfinder.max}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Card 2 */}
          <motion.div
            className="hidden md:block bg-white border-2 border-[#14C5D1] rounded-xl py-9"
            variants={cardVariants}
          >
            <div className="flex flex-col items-center mb-8">
              <div className="items-center justify-center mb-4">
               <img src="/assets/search.svg" alt="DentFinder" />
              </div>
              <h3 className="text-xl font-semibold text-[#14C5D1] mb-3">{t('pricingComparison.dentfinder')}</h3>
              <p className="text-3xl md:text-4xl  font-bold text-[#0C1B1F]">{t('pricingComparison.ourPrice')}</p>
            </div>

            <div className="space-y-0">
              {treatments.map((treatment, index) => (
                <div
                  key={index}
                  className={`py-4 px-6 ${index % 2 === 0 ? 'bg-[#FAFAFA]' : 'bg-white'} ${index !== treatments.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div className="flex justify-center">
                    <span className="text-sm font-semibold text-[#535862]">
                      {treatment.dentfinder.min === treatment.dentfinder.max
                        ? `${t('pricingComparison.startsFrom')} €${treatment.dentfinder.min.toLocaleString()}`
                        : `${t('pricingComparison.startsFrom')} €${treatment.dentfinder.min} - €${treatment.dentfinder.max}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Card 3 */}
          <motion.div
            className="bg-transparent rounded-2xl py-9"
            variants={cardVariants}
          >
            <div className="flex flex-col items-center mb-8">
              <div className="items-center justify-center mb-4">
               <img src="/assets/burger.svg" alt="Netherlands" />
              </div>
              <h3 className="text-xl font-semibold text-[#007B83] mb-3">{t('pricingComparison.otherPlatforms')}</h3>
              <p className="text-3xl md:text-4xl font-bold text-[#0C1B1F]">{t('pricingComparison.netherlands')}</p>
            </div>

            <div className="space-y-0">
              {treatments.map((treatment, index) => (
                <div
                  key={index}
                  className={`py-4 px-6 ${index % 2 === 0 ? 'bg-[#FAFAFA]' : 'bg-transparent'} ${index !== treatments.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div className="flex flex-row justify-between md:justify-center items-center">
                    <span className="text-sm font-medium text-[#181D27] md:hidden">
                      {treatment.name}
                    </span>
                    <span className="text-sm font-semibold text-[#535862]">
                      {treatment.otherPlatform.min === treatment.otherPlatform.max
                        ? `${t('pricingComparison.startsFrom')} €${treatment.otherPlatform.min.toLocaleString()}`
                        : `${t('pricingComparison.startsFrom')} €${treatment.otherPlatform.min} - €${treatment.otherPlatform.max}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Call to Action Button */}
        <motion.div
          className="flex justify-center"
          variants={buttonVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <button className="btn-primary" onClick={handleButtonGetStarted}>
            {t('pricingComparison.button')}
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingComparison;

